import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Briefcase, MapPin, Heart, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const JOBS = [
  "Préparateurs de commandes H/F",
  "Manutentionnaire H/F",
  "Caristes H/F",
  "Agent de quai H/F",
  "Responsable d'exploitation H/F",
  "Agent logistique H/F",
];

const REASONS = [
  { icon: Briefcase, title: "Des missions régulières et variées", desc: "Un large choix de missions adaptées à votre profil" },
  { icon: Heart, title: "Un accompagnement personnalisé", desc: "Un suivi individuel tout au long de votre parcours" },
  { icon: MapPin, title: "Des opportunités proches de chez vous", desc: "Des missions dans votre zone géographique" },
  { icon: Star, title: "Une agence spécialisée", desc: "Qui connaît votre métier et valorise votre savoir-faire" },
];

export default function CandidatsPage() {
  return (
    <main data-testid="candidats-page">
      <SEO
        title="Candidats - Donnez du mouvement à votre carrière"
        description="Rejoignez FR INTERIM : missions en logistique adaptées à votre expérience. Préparateur de commandes, cariste, manutentionnaire, chef d'équipe, agent logistique. Postulez maintenant."
        path="/candidats"
      />
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjB3b3JrZXIlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzc2MTczNTkwfDA&ixlib=rb-4.1.0&q=85')" }} />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" className="max-w-2xl">
            <motion.p variants={fadeUp} custom={0} className="text-[#E5CD96] text-xs font-bold uppercase tracking-[0.2em] mb-4">Espace Candidats</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6">
              Donnez du mouvement à votre carrière
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-gray-200 leading-relaxed">
              FR INTERIM vous accompagne dans votre parcours professionnel au sein des métiers de la logistique et du transport.
              Nous vous proposons des missions adaptées à votre expérience, vos compétences et votre disponibilité.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Jobs */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.2em] text-[#662483] mb-3">Nos métiers</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#3F3F3F]">Les métiers proposés</motion.h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {JOBS.map((job, i) => (
              <motion.div key={job} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp} className="flex items-center gap-3 bg-[#FAFAFA] border border-gray-200 p-5 card-hover">
                <CheckCircle className="w-5 h-5 text-[#662483] shrink-0" />
                <span className="text-sm font-medium text-[#3F3F3F]">{job}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join */}
      <section className="py-16 md:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.2em] text-[#662483] mb-3">Vos avantages</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#3F3F3F]">Pourquoi nous rejoindre ?</motion.h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {REASONS.map((item, i) => (
              <motion.div key={item.title} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp} className="bg-white border border-gray-200 p-6 md:p-8 card-hover">
                <div className="w-12 h-12 bg-gradient-to-r from-[#662483] to-[#0069B4] flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-[#3F3F3F] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-12 bg-[#F0E9F3] p-8">
            <motion.p variants={fadeUp} custom={0} className="text-base text-gray-700 leading-relaxed text-center max-w-2xl mx-auto">
              Chez FR INTERIM, votre savoir-faire est reconnu et valorisé. Nous vous aidons à trouver des missions
              qui vous correspondent et à évoluer dans votre secteur.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="brand-gradient py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Prêt(e) à embarquer ?</h2>
          <p className="text-lg text-white/80 mb-8">Envoyez-nous votre CV dès maintenant !</p>
          <Link to="/auth">
            <Button className="bg-[#E5CD96] text-[#3F3F3F] hover:bg-[#B8A478] font-semibold px-8 py-3 rounded-none text-base" data-testid="candidats-cta">
              S'inscrire maintenant <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
